import classNames from "classnames";
import dayjs from "dayjs";
import { Button } from "components/DesignSystem/Button/Button";
import { MMM_D } from "constants/date";
import {
  CLICKED_FILING_DOT_ON_FILINGS_TIMELINE,
  CLICKED_FILING_NAME_ON_FILINGS_TIMELINE,
} from "constants/analyticsEvents";
import {
  TIMELINE_LABEL_LEAD_MS,
  TIMELINE_LABEL_STAGGER_MS,
  TIMELINE_LABEL_WIDTH,
  TIMELINE_NAME_ROW_BOTTOM,
  TIMELINE_NAMES_PER_MARKER,
} from "constants/filingsTimeline";
import { useAnalytics } from "hooks/useAnalytics";
import { TimelineFilingRow } from "store/apis/filingsTimeline";
import { getFilingShortName } from "utils/filingsTimeline/getFilingShortName";
import { getSweepDelay } from "utils/filingsTimeline/getSweepDelay";
import { getTrackPosition } from "utils/filingsTimeline/getTrackPosition";
import { matchesLegendFilter } from "utils/filingsTimeline/matchesLegendFilter";
import { TimelineMarkerGroup } from "utils/filingsTimeline/groupFilingsByProximity";
import { TimelineClusterDropdown } from "./TimelineClusterDropdown";
import { useTimelineContext } from "./TimelineContext";
import { TimelineFilingTooltip } from "./TimelineFilingTooltip";
import { TimelineTrackDot } from "./TimelineTrackDot";

type TimelineMarkerProps = {
  group: TimelineMarkerGroup;
};

const LABEL_BUTTON_CLASS =
  "!t-h-[18px] !t-max-w-full !t-min-h-0 !t-rounded !t-bg-surface !t-px-1 !t-py-0 !t-text-[11px] !t-font-normal !t-leading-[16.5px] hover:!t-bg-neutral-0 hover:!t-shadow-xs hover:!t-no-underline focus:!t-ring-0 focus-visible:!t-ring-2 focus-visible:!t-ring-purple-20 [&>div>div]:t-min-w-0 [&>div>div]:t-w-full [&>div]:t-min-w-0 [&>div]:t-w-full";

const END_ALIGN_FRACTION = 0.9;
const START_ALIGN_FRACTION = 0.06;

export const TimelineMarker = ({ group }: TimelineMarkerProps) => {
  const { selectedRowKey, onSelectFiling, fadeFilter, isIntroAnimated } =
    useTimelineContext();
  const { trackEvent } = useAnalytics();

  const hasSelection = Boolean(selectedRowKey);
  const dotDelayMs = getSweepDelay(group.fraction);
  const hiddenCount = group.filings.length - TIMELINE_NAMES_PER_MARKER;
  const hasShowAll = hiddenCount > 0;
  const visibleFilings = group.filings.slice(0, TIMELINE_NAMES_PER_MARKER);

  let alignmentClass = "-t-translate-x-1/2";
  if (group.fraction > END_ALIGN_FRACTION) {
    alignmentClass = "-t-translate-x-full";
  }
  if (group.fraction < START_ALIGN_FRACTION) {
    alignmentClass = "";
  }

  const isEdgeAligned = alignmentClass !== "-t-translate-x-1/2";

  const isFaded = (filing: TimelineFilingRow): boolean =>
    fadeFilter !== null && !matchesLegendFilter(filing, fadeFilter);

  const isDateFaded = group.filings.every(isFaded);

  const handleSelectFilingDot = (filing: TimelineFilingRow) => () => {
    trackEvent(CLICKED_FILING_DOT_ON_FILINGS_TIMELINE, {
      filing_name: filing.name,
    });
    onSelectFiling(filing);
  };

  const handleSelectFilingName = (filing: TimelineFilingRow) => () => {
    trackEvent(CLICKED_FILING_NAME_ON_FILINGS_TIMELINE, {
      filing_name: filing.name,
    });
    onSelectFiling(filing);
  };

  const labelDelayMs = (index: number): number =>
    dotDelayMs + TIMELINE_LABEL_LEAD_MS + index * TIMELINE_LABEL_STAGGER_MS;

  // Markers remounted by a filter change skip the intro so the track
  // doesn't blank out and replay the sweep.
  const introClass = isIntroAnimated
    ? "t-animate-label-in motion-reduce:t-animate-none"
    : "";

  const getIntroStyle = (index: number) =>
    isIntroAnimated
      ? { animationDelay: `${labelDelayMs(index)}ms` }
      : undefined;

  const nameLabels = (
    <div
      className={classNames(
        "t-absolute t-left-0 t-flex t-flex-col t-gap-px",
        alignmentClass,
        isEdgeAligned || hasShowAll
          ? "t-items-start t-text-left"
          : "t-items-center t-text-center"
      )}
      style={{ width: TIMELINE_LABEL_WIDTH, bottom: TIMELINE_NAME_ROW_BOTTOM }}
    >
      {visibleFilings.map((filing, index) => (
        <div
          key={filing.rowKey}
          className={classNames("t-max-w-full", introClass)}
          style={getIntroStyle(index)}
        >
          <TimelineFilingTooltip filing={filing}>
            <Button
              customType="link_neutral"
              size="xxsmall"
              onClick={handleSelectFilingName(filing)}
              className={classNames(LABEL_BUTTON_CLASS, {
                "t-opacity-40": isFaded(filing),
                "!t-text-purple": filing.rowKey === selectedRowKey,
                "!t-text-neutral-40":
                  hasSelection && filing.rowKey !== selectedRowKey,
                "!t-text-text-60":
                  !hasSelection && filing.rowKey !== selectedRowKey,
              })}
            >
              <span className="t-block t-w-full t-truncate">
                {getFilingShortName(filing)}
              </span>
            </Button>
          </TimelineFilingTooltip>
        </div>
      ))}

      {hasShowAll && (
        <div
          className={introClass}
          style={getIntroStyle(visibleFilings.length)}
        >
          <TimelineClusterDropdown filings={group.filings} />
        </div>
      )}
    </div>
  );

  return (
    <div
      className="t-absolute t-inset-y-0 t-w-0"
      style={{ left: getTrackPosition(group.fraction) }}
    >
      {nameLabels}

      <div className="t-absolute t-bottom-[74px] t-left-0 t-flex -t-translate-x-1/2 t-translate-y-1/2 t-items-center">
        {group.filings.map((filing) => (
          <TimelineTrackDot
            key={filing.rowKey}
            filing={filing}
            isSelected={filing.rowKey === selectedRowKey}
            isFaded={isFaded(filing)}
            delayMs={dotDelayMs}
            onSelect={handleSelectFilingDot(filing)}
          />
        ))}
      </div>

      <div
        className={classNames(
          "t-absolute t-bottom-[54px] t-left-0 -t-translate-x-1/2 t-transition-opacity t-duration-150 motion-reduce:t-transition-none",
          { "t-opacity-40": isDateFaded }
        )}
      >
        <div
          className={classNames(
            "t-whitespace-nowrap t-text-caption t-text-neutral-40",
            introClass
          )}
          style={getIntroStyle(0)}
        >
          {dayjs(group.filings[0].deadline).format(MMM_D)}
        </div>
      </div>
    </div>
  );
};
